import React, { useState } from "react";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import "./createReportPdf.scss";
import { PDFDocument, rgb } from "pdf-lib";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";

const CreateReportPdf = () => {
  const [isSidebar, setIsSidebar] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [studentData, setStudentData] = useState({
    // Basic Info
    studentName: "",
    registrationNo: "",
    class: "",
    institution: "",

    // 10 Subject rows
    subjects: Array.from({ length: 10 }, () => ({
      sNo: "",
      subject: "",
      maxMarks: "",
      marksObtained: "",
      grade: "",
    })),

    // Auto-calculated totals (will be calculated)
    totalMarks: 0,
    marksAchieved: 0,
    percentage: 0,

    // Additional Info
    academicYear: "",
    classTeacher: "",
    gradingPeriod: "",

    // Attendance
    schoolDays: "",
    daysAttended: "",
    attendancePercentage: 0,

    // Conduct Grades (dropdown values)
    conduct: {
      attentive: "",
      punctual: "",
      neatOrderly: "",
      cooperative: "",
      respectful: "",
      responsible: "",
    },

    // Additional fields
    remarks: "",
    coordinatorName: "",
  });

  // Grade options for conduct
  const conductGrades = [
    { value: "", label: "Select Grade" },
    { value: "O", label: "O - Outstanding" },
    { value: "E", label: "E - Excellent" },
    { value: "G", label: "G - Good" },
    { value: "M", label: "M - Marginal" },
    { value: "S", label: "S - Satisfactory" },
    { value: "NSH", label: "NSH - Need Special Help" },
  ];

  // Function to calculate grade based on marks percentage
  const calculateGrade = (marksObtained, maxMarks) => {
    if (!marksObtained || !maxMarks || maxMarks === 0) return "";

    const percentage = (parseInt(marksObtained) / parseInt(maxMarks)) * 100;

    if (percentage >= 95) return "A+";
    if (percentage >= 85) return "A";
    if (percentage >= 75) return "B+";
    if (percentage >= 65) return "B";
    if (percentage >= 55) return "C+";
    if (percentage >= 45) return "C";
    if (percentage >= 33) return "D";
    return "E";
  };

  // PDF coordinate mapping based on the template image
  const fieldCoordinates = {
    // Basic student info (top section)
    studentName: { x: 120, y: 490 },
    registrationNo: { x: 120, y: 465 },
    class: { x: 120, y: 445 },
    institution: { x: 120, y: 425 },

    // Right side info
    academicYear: { x: 220, y: 385 },
    classTeacher: { x: 220, y: 363 },
    gradingPeriod: { x: 220, y: 342 },

    // Subject table rows (starting from top row)
    subjects: [
      // Row 1
      {
        sNo: { x: 36, y: 285 },
        subject: { x: 60, y: 285 },
        maxMarks: { x: 195, y: 285 },
        marksObtained: { x: 260, y: 285 },
        grade: { x: 330, y: 285 },
      },
      // Row 2
      {
        sNo: { x: 36, y: 265 },
        subject: { x: 60, y: 265 },
        maxMarks: { x: 195, y: 265 },
        marksObtained: { x: 260, y: 265 },
        grade: { x: 330, y: 265 },
      },
      // Row 3
      {
        sNo: { x: 36, y: 246 },
        subject: { x: 60, y: 246 },
        maxMarks: { x: 195, y: 246 },
        marksObtained: { x: 260, y: 246 },
        grade: { x: 330, y: 246 },
      },
      // Row 4
      {
        sNo: { x: 36, y: 226 },
        subject: { x: 60, y: 226 },
        maxMarks: { x: 195, y: 226 },
        marksObtained: { x: 260, y: 226 },
        grade: { x: 330, y: 226 },
      },
      // Row 5
      {
        sNo: { x: 36, y: 206 },
        subject: { x: 60, y: 206 },
        maxMarks: { x: 195, y: 206 },
        marksObtained: { x: 260, y: 206 },
        grade: { x: 330, y: 206 },
      },
      // Row 6
      {
        sNo: { x: 36, y: 187 },
        subject: { x: 60, y: 187 },
        maxMarks: { x: 195, y: 187 },
        marksObtained: { x: 260, y: 187 },
        grade: { x: 330, y: 187 },
      },
      // Row 7
      {
        sNo: { x: 36, y: 169 },
        subject: { x: 60, y: 169 },
        maxMarks: { x: 195, y: 169 },
        marksObtained: { x: 260, y: 169 },
        grade: { x: 330, y: 169 },
      },
      // Row 8
      {
        sNo: { x: 36, y: 148 },
        subject: { x: 60, y: 148 },
        maxMarks: { x: 195, y: 148 },
        marksObtained: { x: 260, y: 148 },
        grade: { x: 330, y: 148 },
      },
      // Row 9
      {
        sNo: { x: 36, y: 128 },
        subject: { x: 60, y: 128 },
        maxMarks: { x: 195, y: 128 },
        marksObtained: { x: 260, y: 128 },
        grade: { x: 330, y: 128 },
      },
      // Row 10
      {
        sNo: { x: 36, y: 110 },
        subject: { x: 60, y: 110 },
        maxMarks: { x: 195, y: 110 },
        marksObtained: { x: 260, y: 110 },
        grade: { x: 330, y: 110 },
      },
    ],

    // Total section
    totalMarks: { x: 190, y: 90 },
    marksAchieved: { x: 190, y: 70 },
    percentage: { x: 190, y: 45 },

    // Student Statistics (right side)
    schoolDays: { x: 610, y: 468 },
    daysAttended: { x: 610, y: 446 },
    attendancePercentage: { x: 610, y: 425 },

    // Conduct grades (right side)
    conduct: {
      attentive: { x: 610, y: 360 },
      punctual: { x: 610, y: 338 },
      neatOrderly: { x: 610, y: 316 },
      cooperative: { x: 610, y: 296 },
      respectful: { x: 610, y: 276 },
      responsible: { x: 610, y: 252 },
    },

    // Additional fields
    remarks: { x: 478, y: 43 },
    coordinatorName: { x: 690, y: 32 },
  };

  // Simple PDF preview with data display
  const getPreviewData = () => {
    const data = [];

    // Basic Information
    if (studentData.studentName)
      data.push({
        label: "Student Name",
        value: studentData.studentName,
        coords: fieldCoordinates.studentName,
      });
    if (studentData.registrationNo)
      data.push({
        label: "Registration No",
        value: studentData.registrationNo,
        coords: fieldCoordinates.registrationNo,
      });
    if (studentData.class)
      data.push({
        label: "Class",
        value: studentData.class,
        coords: fieldCoordinates.class,
      });
    if (studentData.institution)
      data.push({
        label: "Institution",
        value: studentData.institution,
        coords: fieldCoordinates.institution,
      });
    if (studentData.academicYear)
      data.push({
        label: "Academic Year",
        value: studentData.academicYear,
        coords: fieldCoordinates.academicYear,
      });
    if (studentData.classTeacher)
      data.push({
        label: "Class Teacher",
        value: studentData.classTeacher,
        coords: fieldCoordinates.classTeacher,
      });
    if (studentData.gradingPeriod)
      data.push({
        label: "Grading Period",
        value: studentData.gradingPeriod,
        coords: fieldCoordinates.gradingPeriod,
      });

    // Attendance
    if (studentData.schoolDays)
      data.push({
        label: "School Days",
        value: studentData.schoolDays,
        coords: fieldCoordinates.schoolDays,
      });
    if (studentData.daysAttended)
      data.push({
        label: "Days Attended",
        value: studentData.daysAttended,
        coords: fieldCoordinates.daysAttended,
      });
    if (studentData.attendancePercentage)
      data.push({
        label: "Attendance %",
        value: studentData.attendancePercentage + "%",
        coords: fieldCoordinates.attendancePercentage,
      });

    // Subjects
    studentData.subjects.forEach((subject, index) => {
      if (subject.sNo)
        data.push({
          label: `Subject ${index + 1} - S.No`,
          value: subject.sNo,
          coords: fieldCoordinates.subjects[index]?.sNo,
        });
      if (subject.subject)
        data.push({
          label: `Subject ${index + 1} - Name`,
          value: subject.subject,
          coords: fieldCoordinates.subjects[index]?.subject,
        });
      if (subject.maxMarks)
        data.push({
          label: `Subject ${index + 1} - Max Marks`,
          value: subject.maxMarks,
          coords: fieldCoordinates.subjects[index]?.maxMarks,
        });
      if (subject.marksObtained)
        data.push({
          label: `Subject ${index + 1} - Obtained`,
          value: subject.marksObtained,
          coords: fieldCoordinates.subjects[index]?.marksObtained,
        });
      if (subject.grade)
        data.push({
          label: `Subject ${index + 1} - Grade`,
          value: subject.grade,
          coords: fieldCoordinates.subjects[index]?.grade,
        });
    });

    // Totals
    if (studentData.totalMarks)
      data.push({
        label: "Total Marks",
        value: studentData.totalMarks,
        coords: fieldCoordinates.totalMarks,
      });
    if (studentData.marksAchieved)
      data.push({
        label: "Marks Achieved",
        value: studentData.marksAchieved,
        coords: fieldCoordinates.marksAchieved,
      });
    if (studentData.percentage)
      data.push({
        label: "Percentage",
        value: studentData.percentage + "%",
        coords: fieldCoordinates.percentage,
      });

    // Conduct
    Object.entries(studentData.conduct).forEach(([key, value]) => {
      if (value)
        data.push({
          label: `Conduct - ${key.charAt(0).toUpperCase() + key.slice(1)}`,
          value: value,
          coords: fieldCoordinates.conduct[key],
        });
    });

    // Additional fields
    if (studentData.remarks)
      data.push({
        label: "Remarks",
        value: studentData.remarks,
        coords: fieldCoordinates.remarks,
      });
    if (studentData.coordinatorName)
      data.push({
        label: "Coordinator Name",
        value: studentData.coordinatorName,
        coords: fieldCoordinates.coordinatorName,
      });

    return data;
  };

  // Auto-calculate totals whenever subjects change
  const calculateTotals = (subjects) => {
    let totalMarks = 0;
    let marksAchieved = 0;

    subjects.forEach((subject) => {
      if (subject.maxMarks && !isNaN(subject.maxMarks)) {
        totalMarks += parseInt(subject.maxMarks);
      }
      if (subject.marksObtained && !isNaN(subject.marksObtained)) {
        marksAchieved += parseInt(subject.marksObtained);
      }
    });

    const percentage =
      totalMarks > 0 ? ((marksAchieved / totalMarks) * 100).toFixed(2) : 0;

    return { totalMarks, marksAchieved, percentage: parseFloat(percentage) };
  };

  // Auto-calculate attendance percentage
  const calculateAttendancePercentage = (schoolDays, daysAttended) => {
    if (
      schoolDays &&
      daysAttended &&
      !isNaN(schoolDays) &&
      !isNaN(daysAttended)
    ) {
      return ((parseInt(daysAttended) / parseInt(schoolDays)) * 100).toFixed(2);
    }
    return 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("subject-")) {
      // Handle subject array updates
      const [, index, field] = name.split("-");
      const subjectIndex = parseInt(index);

      setStudentData((prev) => {
        const newSubjects = [...prev.subjects];
        newSubjects[subjectIndex] = {
          ...newSubjects[subjectIndex],
          [field]: value,
        };

        // Auto-calculate grade if marks are updated
        if (field === "marksObtained" || field === "maxMarks") {
          const grade = calculateGrade(
            field === "marksObtained"
              ? value
              : newSubjects[subjectIndex].marksObtained,
            field === "maxMarks" ? value : newSubjects[subjectIndex].maxMarks
          );
          newSubjects[subjectIndex].grade = grade;
        }

        // Auto-calculate totals
        const totals = calculateTotals(newSubjects);

        return {
          ...prev,
          subjects: newSubjects,
          ...totals,
        };
      });
    } else if (name.startsWith("conduct-")) {
      // Handle conduct grades
      const conductField = name.replace("conduct-", "");
      setStudentData((prev) => ({
        ...prev,
        conduct: {
          ...prev.conduct,
          [conductField]: value,
        },
      }));
    } else if (name === "schoolDays" || name === "daysAttended") {
      // Handle attendance with auto-calculation
      setStudentData((prev) => {
        const newData = { ...prev, [name]: value };
        const attendancePercentage = calculateAttendancePercentage(
          name === "schoolDays" ? value : prev.schoolDays,
          name === "daysAttended" ? value : prev.daysAttended
        );
        return {
          ...newData,
          attendancePercentage: parseFloat(attendancePercentage),
        };
      });
    } else {
      // Handle regular fields
      setStudentData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const generatePDF = async () => {
    try {
      setIsGenerating(true);

      // Load the existing PDF template
      const existingPdfBytes = await fetch("/ReportCard1.pdf").then((res) =>
        res.arrayBuffer()
      );
      const pdfDoc = await PDFDocument.load(existingPdfBytes);

      // Get the first page
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];

      // Set font and size
      const fontSize = 10;
      const fontColor = rgb(0, 0, 0); // Black color

      // Add basic student information
      if (studentData.studentName) {
        firstPage.drawText(studentData.studentName, {
          x: fieldCoordinates.studentName.x,
          y: fieldCoordinates.studentName.y,
          size: fontSize,
          color: fontColor,
        });
      }

      if (studentData.registrationNo) {
        firstPage.drawText(studentData.registrationNo, {
          x: fieldCoordinates.registrationNo.x,
          y: fieldCoordinates.registrationNo.y,
          size: fontSize,
          color: fontColor,
        });
      }

      if (studentData.class) {
        firstPage.drawText(studentData.class, {
          x: fieldCoordinates.class.x,
          y: fieldCoordinates.class.y,
          size: fontSize,
          color: fontColor,
        });
      }

      if (studentData.institution) {
        firstPage.drawText(studentData.institution, {
          x: fieldCoordinates.institution.x,
          y: fieldCoordinates.institution.y,
          size: fontSize,
          color: fontColor,
        });
      }

      // Add right side information
      if (studentData.academicYear) {
        firstPage.drawText(studentData.academicYear, {
          x: fieldCoordinates.academicYear.x,
          y: fieldCoordinates.academicYear.y,
          size: fontSize,
          color: fontColor,
        });
      }

      if (studentData.classTeacher) {
        firstPage.drawText(studentData.classTeacher, {
          x: fieldCoordinates.classTeacher.x,
          y: fieldCoordinates.classTeacher.y,
          size: fontSize,
          color: fontColor,
        });
      }

      if (studentData.gradingPeriod) {
        firstPage.drawText(studentData.gradingPeriod, {
          x: fieldCoordinates.gradingPeriod.x,
          y: fieldCoordinates.gradingPeriod.y,
          size: fontSize,
          color: fontColor,
        });
      }

      // Add subject rows
      studentData.subjects.forEach((subject, index) => {
        const coords = fieldCoordinates.subjects[index];
        if (!coords) return;

        if (subject.sNo) {
          firstPage.drawText(subject.sNo.toString(), {
            x: coords.sNo.x,
            y: coords.sNo.y,
            size: fontSize,
            color: fontColor,
          });
        }

        if (subject.subject) {
          firstPage.drawText(subject.subject, {
            x: coords.subject.x,
            y: coords.subject.y,
            size: fontSize,
            color: fontColor,
          });
        }

        if (subject.maxMarks) {
          firstPage.drawText(subject.maxMarks.toString(), {
            x: coords.maxMarks.x,
            y: coords.maxMarks.y,
            size: fontSize,
            color: fontColor,
          });
        }

        if (subject.marksObtained) {
          firstPage.drawText(subject.marksObtained.toString(), {
            x: coords.marksObtained.x,
            y: coords.marksObtained.y,
            size: fontSize,
            color: fontColor,
          });
        }

        if (subject.grade) {
          firstPage.drawText(subject.grade, {
            x: coords.grade.x,
            y: coords.grade.y,
            size: fontSize,
            color: fontColor,
          });
        }
      });

      // Add totals
      if (studentData.totalMarks) {
        firstPage.drawText(studentData.totalMarks.toString(), {
          x: fieldCoordinates.totalMarks.x,
          y: fieldCoordinates.totalMarks.y,
          size: fontSize,
          color: fontColor,
        });
      }

      if (studentData.marksAchieved) {
        firstPage.drawText(studentData.marksAchieved.toString(), {
          x: fieldCoordinates.marksAchieved.x,
          y: fieldCoordinates.marksAchieved.y,
          size: fontSize,
          color: fontColor,
        });
      }

      if (studentData.percentage) {
        firstPage.drawText(studentData.percentage.toString() + "%", {
          x: fieldCoordinates.percentage.x,
          y: fieldCoordinates.percentage.y,
          size: fontSize,
          color: fontColor,
        });
      }

      // Add attendance information
      if (studentData.schoolDays) {
        firstPage.drawText(studentData.schoolDays.toString(), {
          x: fieldCoordinates.schoolDays.x,
          y: fieldCoordinates.schoolDays.y,
          size: fontSize,
          color: fontColor,
        });
      }

      if (studentData.daysAttended) {
        firstPage.drawText(studentData.daysAttended.toString(), {
          x: fieldCoordinates.daysAttended.x,
          y: fieldCoordinates.daysAttended.y,
          size: fontSize,
          color: fontColor,
        });
      }

      if (studentData.attendancePercentage) {
        firstPage.drawText(studentData.attendancePercentage.toString() + "%", {
          x: fieldCoordinates.attendancePercentage.x,
          y: fieldCoordinates.attendancePercentage.y,
          size: fontSize,
          color: fontColor,
        });
      }

      // Add conduct grades
      Object.entries(studentData.conduct).forEach(([key, value]) => {
        if (value && fieldCoordinates.conduct[key]) {
          firstPage.drawText(value, {
            x: fieldCoordinates.conduct[key].x,
            y: fieldCoordinates.conduct[key].y,
            size: fontSize,
            color: fontColor,
          });
        }
      });

      // Add additional fields
      if (studentData.remarks) {
        firstPage.drawText(studentData.remarks, {
          x: fieldCoordinates.remarks.x,
          y: fieldCoordinates.remarks.y,
          size: fontSize,
          color: fontColor,
        });
      }

      if (studentData.coordinatorName) {
        firstPage.drawText(studentData.coordinatorName, {
          x: fieldCoordinates.coordinatorName.x,
          y: fieldCoordinates.coordinatorName.y,
          size: fontSize,
          color: fontColor,
        });
      }

      // Save the PDF
      const pdfBytes = await pdfDoc.save();

      // Download the PDF
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${studentData.studentName || "Student"}_ReportCard.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please check if the template file exists.");
    } finally {
      setIsGenerating(false);
    }
  };

  const clearForm = () => {
    setStudentData({
      studentName: "",
      registrationNo: "",
      class: "",
      institution: "",
      subjects: Array.from({ length: 10 }, () => ({
        sNo: "",
        subject: "",
        maxMarks: "",
        marksObtained: "",
        grade: "",
      })),
      totalMarks: 0,
      marksAchieved: 0,
      percentage: 0,
      academicYear: "",
      classTeacher: "",
      gradingPeriod: "",
      schoolDays: "",
      daysAttended: "",
      attendancePercentage: 0,
      conduct: {
        attentive: "",
        punctual: "",
        neatOrderly: "",
        cooperative: "",
        respectful: "",
        responsible: "",
      },
      remarks: "",
      coordinatorName: "",
    });
  };

  return (
    <div className="create-report-pdf">
      <Sidebar isSidebar={isSidebar} />
      <div className="create-report-pdf-container">
        <Navbar setIsSidebar={setIsSidebar} />

        <div className="content">
          <div className="header">
            <h1>Create Report PDF</h1>
            <p>Fill student information to generate report card PDF</p>
          </div>

          <div className="form-section">
            <div className="form-header">
              <PictureAsPdfIcon className="icon" />
              <h2>Report Card Information</h2>
            </div>

            {/* Basic Student Information */}
            <div className="section-title">Basic Information</div>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="studentName">Student's Name *</label>
                <input
                  type="text"
                  id="studentName"
                  name="studentName"
                  value={studentData.studentName}
                  onChange={handleInputChange}
                  placeholder="Enter student's name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="registrationNo">Registration No.</label>
                <input
                  type="text"
                  id="registrationNo"
                  name="registrationNo"
                  value={studentData.registrationNo}
                  onChange={handleInputChange}
                  placeholder="Enter registration number"
                />
              </div>

              <div className="form-group">
                <label htmlFor="class">Class</label>
                <input
                  type="text"
                  id="class"
                  name="class"
                  value={studentData.class}
                  onChange={handleInputChange}
                  placeholder="e.g., 5th, 10th"
                />
              </div>

              <div className="form-group">
                <label htmlFor="institution">Institution</label>
                <input
                  type="text"
                  id="institution"
                  name="institution"
                  value={studentData.institution}
                  onChange={handleInputChange}
                  placeholder="Enter institution name"
                />
              </div>
            </div>

            {/* Subject Table */}
            <div className="section-title">Subjects (10 Rows)</div>
            <div className="subjects-table">
              <div className="table-header">
                <div className="col-sno">S.No</div>
                <div className="col-subject">Subject</div>
                <div className="col-max">Max Marks</div>
                <div className="col-obtained">Marks Obtained</div>
                <div className="col-remarks">Grades</div>
              </div>

              {studentData.subjects.map((subject, index) => (
                <div key={index} className="table-row">
                  <div className="col-sno">
                    <input
                      type="text"
                      name={`subject-${index}-sNo`}
                      value={subject.sNo}
                      onChange={handleInputChange}
                      placeholder="S.No"
                    />
                  </div>
                  <div className="col-subject">
                    <input
                      type="text"
                      name={`subject-${index}-subject`}
                      value={subject.subject}
                      onChange={handleInputChange}
                      placeholder="Subject name"
                    />
                  </div>
                  <div className="col-max">
                    <input
                      type="number"
                      name={`subject-${index}-maxMarks`}
                      value={subject.maxMarks}
                      onChange={handleInputChange}
                      placeholder="Max Mark"
                      min="0"
                    />
                  </div>
                  <div className="col-obtained">
                    <input
                      type="number"
                      name={`subject-${index}-marksObtained`}
                      value={subject.marksObtained}
                      onChange={handleInputChange}
                      placeholder="Marks obtained"
                      min="0"
                    />
                  </div>
                  <div className="col-remarks">
                    <div className="grade-display">{subject.grade || "-"}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Auto-calculated Totals */}
            <div className="totals-section">
              <div className="total-item">
                <label>Total Marks:</label>
                <span className="calculated-value">
                  {studentData.totalMarks}
                </span>
              </div>
              <div className="total-item">
                <label>Marks Achieved:</label>
                <span className="calculated-value">
                  {studentData.marksAchieved}
                </span>
              </div>
              <div className="total-item">
                <label>Percentage:</label>
                <span className="calculated-value">
                  {studentData.percentage}%
                </span>
              </div>
            </div>

            {/* Additional Information */}
            <div className="section-title">Additional Information</div>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="academicYear">Academic Year</label>
                <input
                  type="text"
                  id="academicYear"
                  name="academicYear"
                  value={studentData.academicYear}
                  onChange={handleInputChange}
                  placeholder="e.g., 2023-2024"
                />
              </div>

              <div className="form-group">
                <label htmlFor="classTeacher">Name of Class Teacher</label>
                <input
                  type="text"
                  id="classTeacher"
                  name="classTeacher"
                  value={studentData.classTeacher}
                  onChange={handleInputChange}
                  placeholder="Enter teacher's name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="gradingPeriod">Grading Period</label>
                <input
                  type="text"
                  id="gradingPeriod"
                  name="gradingPeriod"
                  value={studentData.gradingPeriod}
                  onChange={handleInputChange}
                  placeholder="e.g., 1st Term, Mid-term"
                />
              </div>
            </div>

            {/* Attendance Information */}
            <div className="section-title">Student Statistics</div>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="schoolDays">School Days</label>
                <input
                  type="number"
                  id="schoolDays"
                  name="schoolDays"
                  value={studentData.schoolDays}
                  onChange={handleInputChange}
                  placeholder="School Days"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="daysAttended">Days Attended</label>
                <input
                  type="number"
                  id="daysAttended"
                  name="daysAttended"
                  value={studentData.daysAttended}
                  onChange={handleInputChange}
                  placeholder="Days Attended"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>Attendance Percentage</label>
                <div className="calculated-field">
                  {studentData.attendancePercentage}%
                </div>
              </div>
            </div>

            {/* Conduct Grades */}
            <div className="section-title">Conduct</div>
            <div className="conduct-grid">
              <div className="conduct-item">
                <label htmlFor="conduct-attentive">1. Attentive</label>
                <select
                  id="conduct-attentive"
                  name="conduct-attentive"
                  value={studentData.conduct.attentive}
                  onChange={handleInputChange}
                >
                  {conductGrades.map((grade) => (
                    <option key={grade.value} value={grade.value}>
                      {grade.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="conduct-item">
                <label htmlFor="conduct-punctual">2. Punctual</label>
                <select
                  id="conduct-punctual"
                  name="conduct-punctual"
                  value={studentData.conduct.punctual}
                  onChange={handleInputChange}
                >
                  {conductGrades.map((grade) => (
                    <option key={grade.value} value={grade.value}>
                      {grade.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="conduct-item">
                <label htmlFor="conduct-neatOrderly">3. Neat & Orderly</label>
                <select
                  id="conduct-neatOrderly"
                  name="conduct-neatOrderly"
                  value={studentData.conduct.neatOrderly}
                  onChange={handleInputChange}
                >
                  {conductGrades.map((grade) => (
                    <option key={grade.value} value={grade.value}>
                      {grade.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="conduct-item">
                <label htmlFor="conduct-cooperative">4. Cooperative</label>
                <select
                  id="conduct-cooperative"
                  name="conduct-cooperative"
                  value={studentData.conduct.cooperative}
                  onChange={handleInputChange}
                >
                  {conductGrades.map((grade) => (
                    <option key={grade.value} value={grade.value}>
                      {grade.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="conduct-item">
                <label htmlFor="conduct-respectful">5. Respectful</label>
                <select
                  id="conduct-respectful"
                  name="conduct-respectful"
                  value={studentData.conduct.respectful}
                  onChange={handleInputChange}
                >
                  {conductGrades.map((grade) => (
                    <option key={grade.value} value={grade.value}>
                      {grade.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="conduct-item">
                <label htmlFor="conduct-responsible">6. Responsible</label>
                <select
                  id="conduct-responsible"
                  name="conduct-responsible"
                  value={studentData.conduct.responsible}
                  onChange={handleInputChange}
                >
                  {conductGrades.map((grade) => (
                    <option key={grade.value} value={grade.value}>
                      {grade.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Additional Fields */}
            {/* <div className="section-title">Additional Information</div>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="coordinatorName">Coordinator Name</label>
                <input
                  type="text"
                  id="coordinatorName"
                  name="coordinatorName"
                  value={studentData.coordinatorName}
                  onChange={handleInputChange}
                  placeholder="Enter coordinator name"
                />
              </div>
            </div>

            <div className="textarea-group">
              <label htmlFor="remarks">Remarks</label>
              <textarea
                id="remarks"
                name="remarks"
                value={studentData.remarks}
                onChange={handleInputChange}
                placeholder="Enter remarks and comments"
                rows="4"
              />
            </div> */}

            <div className="button-group">
              <button
                type="button"
                className="btn-secondary"
                onClick={clearForm}
              >
                Clear Form
              </button>

              <button
                type="button"
                className="btn-primary"
                onClick={generatePDF}
                disabled={isGenerating || !studentData.studentName}
              >
                {isGenerating ? (
                  <>Generating...</>
                ) : (
                  <>
                    <DownloadIcon className="btn-icon" />
                    Download Report Card PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateReportPdf;
